import api from './client'
import type { CategoryListResponse } from './categories'
import type { CategorySpend } from './dashboard'

export type BudgetPeriodType = 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'YEARLY'

export interface SpendingSummary {
  totalExpenses: number
  budgetLimit: number | null
  /** Of countedExpenses (#458). */
  percentUsed: number | null
  remaining: number | null
  // Optional: the web app can deploy before the API that adds them. countedExpenses is the spend
  // measured against the monthly budget — everything except spend on yearly-budget categories.
  countedExpenses?: number
  yearlyBudgetedExpenses?: number
}

export interface SavingsSummary {
  income: number
  expenses: number
  actualSavings: number
  savingsGoal: number | null
  percentOfGoal: number | null
}

export interface CategoryBudget {
  categoryId: string
  name: string
  icon: string
  limit: number
  spent: number
  percentUsed: number
  isOverBudget: boolean
}

export interface MonthSpend {
  /** yyyy-MM */
  month: string
  spent: number
}

/** A yearly budget over the financial year up to the selected period's end. */
export interface YearlyCategoryBudget {
  categoryId: string
  name: string
  icon: string
  limit: number
  spentBefore: number
  spentInPeriod: number
  spentToDate: number
  isOverBudget: boolean
  months: MonthSpend[]
}

export interface YearlyBudgets {
  fyLabel: string
  fyStart: string
  fyEnd: string
  monthsElapsed: number
  categories: YearlyCategoryBudget[]
}

export interface BudgetResponse {
  periodType: BudgetPeriodType
  period: string
  spending: SpendingSummary
  savings: SavingsSummary
  categories: CategoryBudget[]
  unbudgetedSpend: number
  // Optional: the web app can deploy before the API that adds them.
  spendingByCategory?: CategorySpend[]
  incomeByCategory?: CategorySpend[]
  /** The same breakdowns for the period just before, same period type. */
  previousSpendingByCategory?: CategorySpend[]
  previousIncomeByCategory?: CategorySpend[]
  /** Null when no category has a yearly budget. */
  yearly?: YearlyBudgets | null
}

export function fetchBudget(params?: {
  periodType?: BudgetPeriodType
  period?: string
}): Promise<BudgetResponse> {
  return api.get('/budget', { params: params ?? {} }).then((r) => r.data)
}

export type CategoryBudgetPeriod = 'MONTHLY' | 'YEARLY'

/** Sets every listed category's budget in one go — all apply or none do. A null amount clears it. */
export function setCategoryBudgets(
  budgets: { categoryId: string; period: CategoryBudgetPeriod; amount: number | null }[]
): Promise<CategoryListResponse> {
  return api.put('/budget/categories', { budgets }).then((r) => r.data)
}
